import {useTranslation} from 'react-i18next';
import dayjs from 'dayjs';
import React from 'react';
import {Avatar, Button, Dropdown, Space, Spin, Tooltip} from 'antd';
import ls from 'localstorage-slim';
import {InfoCircleOutlined, LoadingOutlined} from '@ant-design/icons';
import {
  cardLimitPeriodConstants,
  subscriptionCategoriesConstants as subsOptions,
  subscriptionBillTypesConstants,
  subscriptionBudgetStatusesConstants,
  subscriptionPaymentTypesConstants,
  subscriptionStatusesConstants,
  subscriptionDurationTypesConstants,
  subscriptionReviewTypesConstants,
  subscriptionFormFields,
  transactionsRequestLimit,
  subscriptionContractRenewalPeriodTypesConstants,
  subscriptionContractRenewalReminderTypesConstants,
  subscriptionPaymentFrequencyTypesConstants,
  subscriptionServiceSearchMinLength,
} from '../../constants';
import {ChevronDownIcon, EllipsisVerticalIcon} from '../../icons';
import routes from '../../routes/routes.json';
import {
  StyledSubscriptionTableTabMenuItemSpace
} from '../../components/pages/SubscriptionsPage/tabComponents/SubscriptionTable/StyledSubscriptionTable';
import NameWithLink from '../../components/NameWithLink';
import EditButton from '../../components/EditButton';
import Usage from '../../components/pages/SubscriptionPage/Usage';
import SubscriptionStatusMark from '../../components/pages/SubscriptionsPage/SubscriptionStatusMark';
import ExpandedSubscriptionRow from '../../components/pages/SubscriptionsPage/tabComponents/ExpandedSubscriptionRow';
import StatusLine from '../../components/StatusLine';
import {amountHelpers} from './amount.helpers';
import {cardsHelpers} from './cards.helpers';
import {dateHelpers} from './date.helpers';
import {elementHelpers} from './element.helpers';
import {expensesHelpers} from './expenses.helpers';
import {fileHelpers} from './file.helpers';
import {OptionsList} from './form.helpers';
import {kycHelpers} from './kyc.helpers';
import {locationHelpers} from './location.helpers';
import {objectHelpers} from './object.helpers';
import {teamsHelpers} from './teams.helpers';
import {textHelpers} from './text.helpers';

const gObjProp = objectHelpers.getObjProp;

const {ABOUT_TO_RENEW, ACTIVE, AUDITED, CARD_ERROR, DELETED, INACTIVE, PENDING, RESTRICTED, TERMINATED,
  TRIAL, UNTRACKED, UNMANAGED} = subscriptionStatusesConstants;

const {OVERBUDGET} = subscriptionBudgetStatusesConstants;

const {CARD, FREE, NOT_APPLICABLE, WIRE} = subscriptionPaymentTypesConstants;

const storedTabColumnsPrefix = 'stored-subscriptions-tab-columns-v3';

const alwaysAvailableColumnKeys = ['vendor'];

const subscriptionStatuses = {
  [PENDING]: {
    key: 'subscriptionStatuses.waiting',
    color: '#D9D9D9'
  },
  [ACTIVE]: {
    key: 'subscriptionStatuses.active',
    color: '#52C41A'
  },
  [INACTIVE]: {
    key: 'subscriptionStatuses.halted',
    color: '#FFCC00'
  },
  [TERMINATED]: {
    key: 'subscriptionStatuses.deleted',
    color: '#FF7875'
  },
  [DELETED]: {
    key: 'subscriptionStatuses.deleted',
    color: '#FF7875'
  },
  [CARD_ERROR]: {
    key: 'subscriptionStatuses.cardError',
    color: '#FF7875'
  },
  [TRIAL]: {
    key: 'subscriptionStatuses.trial',
    color: '#1890FF'
  },
  [UNTRACKED]: {
    key: 'subscriptionStatuses.untracked',
    color: '#9493A6'
  },
  [AUDITED]: {
    key: 'subscriptionStatuses.audited',
    color: '#722ED1'
  },
  [RESTRICTED]: {
    key: 'subscriptionStatuses.restricted',
    color: '#FFCC00'
  },
  [UNMANAGED]: {
    key: 'subscriptionStatuses.unmanaged',
    color: '#D9D9D9'
  },
  [OVERBUDGET]: {
    key: 'subscriptionStatuses.overBudget',
    color: '#FAAD14'
  },
  [ABOUT_TO_RENEW]: {
    key: 'subscriptionStatuses.aboutToRenew',
    color: '#A020F0'
  },
};

const billTypeKeys = {
  [subscriptionBillTypesConstants.MONTHLY]: 'billTypes.monthly',
  [subscriptionBillTypesConstants.YEARLY]: 'billTypes.yearly',
  [subscriptionBillTypesConstants.PASS_AS_YOU_GO]: 'billTypes.fixed',
};

const durationTypeKeys = {
  [subscriptionDurationTypesConstants.MONTHS]: 'subscriptionDurationTypes.months',
  [subscriptionDurationTypesConstants.YEARS]: 'subscriptionDurationTypes.years',
};

const cardLimitPeriodTypeKeys = {
  [cardLimitPeriodConstants.MONTH]: 'cardLimitPeriodTypes.monthly',
  [cardLimitPeriodConstants.YEAR]: 'cardLimitPeriodTypes.yearly',
  [cardLimitPeriodConstants.ALL]: 'cardLimitPeriodTypes.fixed'
};

const contractRenewalPeriodTypeKeys = {
  [subscriptionContractRenewalPeriodTypesConstants.ONE_MONTH]: 'contractRenewalPeriodTypes.oneMonth',
  [subscriptionContractRenewalPeriodTypesConstants.THREE_MONTHS]: 'contractRenewalPeriodTypes.threeMonths',
  [subscriptionContractRenewalPeriodTypesConstants.SIX_MONTHS]: 'contractRenewalPeriodTypes.sixMonths',
  [subscriptionContractRenewalPeriodTypesConstants.TWELVE_MONTHS]: 'contractRenewalPeriodTypes.twelveMonths',
  [subscriptionContractRenewalPeriodTypesConstants.EIGHTEEN_MONTHS]: 'contractRenewalPeriodTypes.eighteenMonths',
  [subscriptionContractRenewalPeriodTypesConstants.TWENTY_FOUR_MONTHS]: 'contractRenewalPeriodTypes.twentyFourMonths',
  [subscriptionContractRenewalPeriodTypesConstants.THIRTY_SIX_MONTHS]: 'contractRenewalPeriodTypes.thirtySixMonths'
};

const contractRenewalReminderTypeKeys = {
  [subscriptionContractRenewalReminderTypesConstants.NEVER]: 'contractRenewalReminderTypes.never',
  [subscriptionContractRenewalReminderTypesConstants.TWO_WEEKS]: 'contractRenewalReminderTypes.twoWeeks',
  [subscriptionContractRenewalReminderTypesConstants.ONE_MONTH]: 'contractRenewalReminderTypes.oneMonth',
  [subscriptionContractRenewalReminderTypesConstants.TWO_MONTHS]: 'contractRenewalReminderTypes.twoMonths',
  [subscriptionContractRenewalReminderTypesConstants.THREE_MONTHS]: 'contractRenewalReminderTypes.threeMonths',
  [subscriptionContractRenewalReminderTypesConstants.SIX_MONTHS]: 'contractRenewalReminderTypes.sixMonths'
};

const paymentFrequencyTypeKeys = {
  [subscriptionPaymentFrequencyTypesConstants.ONE_MONTH]: 'paymentFrequencyTypes.oneMonth',
  [subscriptionPaymentFrequencyTypesConstants.THREE_MONTHS]: 'paymentFrequencyTypes.threeMonths',
  [subscriptionPaymentFrequencyTypesConstants.SIX_MONTHS]: 'paymentFrequencyTypes.sixMonths',
  [subscriptionPaymentFrequencyTypesConstants.TWELVE_MONTHS]: 'paymentFrequencyTypes.twelveMonths'
};

const paymentTypeKeys = {
  [CARD]: 'subscriptionPaymentTypes.1',
  [FREE]: 'subscriptionPaymentTypes.0',
  [WIRE]: 'subscriptionPaymentTypes.3',
  [NOT_APPLICABLE]: 'subscriptionPaymentTypes.2'
};

const subscriptionReviewTypeKeys = {
  [subscriptionReviewTypesConstants.FREE]: 'subscriptionReviewTypesConstants.free',
  [subscriptionReviewTypesConstants.PAID]: 'subscriptionReviewTypesConstants.paid',
};

const subscriptionStatusKeys = {
  [ACTIVE]: 'subscriptionStatuses.active',
  [TRIAL]: 'subscriptionStatuses.trial',
  // [UNTRACKED]: 'subscriptionStatuses.untracked'
};

const categories = {
  [subsOptions.ANALYTICS_TOOLS_SOFTWARE]: {
    key: 'subscriptionCategories.analytics_tools_&_software',
    color: '#EF7E32',
    bgColor: '#FDF2EA'
  },
  [subsOptions.ARTIFICIAL_INTELLIGENCE_SOFTWARE]: {
    key: 'subscriptionCategories.artificial_intelligence_software',
    color: '#C02323',
    bgColor: '#F8E9E9'
  },
  [subsOptions.AR_VR]: {
    key: 'subscriptionCategories.ar_vr_software',
    color: '#03AB93',
    bgColor: '#E5F6F4'
  },
  [subsOptions.B2B_MARKETPLACES]: {
    key: 'subscriptionCategories.b2b_marketplaces',
    color: '#19AADE',
    bgColor: '#E8F6FB'
  },
  [subsOptions.CAD_PLM_SOFTWARE]: {
    key: 'subscriptionCategories.cad_plm_software',
    color: '#AF4BCE',
    bgColor: '#F7EDFA'
  },
  [subsOptions.COLLABORATION_PRODUCTIVITY_SOFTWARE]: {
    key: 'subscriptionCategories.collaboration_&_productivity_software',
    color: '#19AADE',
    bgColor: '#E8F6FB'
  },
  [subsOptions.COMMERCE_SOFTWARE]: {
    key: 'subscriptionCategories.commerce_software',
    color: '#C02323',
    bgColor: '#F8E9E9'
  },
  [subsOptions.CONTENT_MANAGEMENT_SYSTEMS]: {
    key: 'subscriptionCategories.content_management_systems',
    color: '#EABD3B',
    bgColor: '#FCF8EB'
  },
  [subsOptions.CUSTOMER_SERVICE_SOFTWARE]: {
    key: 'subscriptionCategories.customer_service_software',
    color: '#03AB93',
    bgColor: '#E5F6F4'
  },
  [subsOptions.DATA_PRIVACY_SOFTWARE]: {
    key: 'subscriptionCategories.data_privacy_software',
    color: '#DB4CB2',
    bgColor: '#FBEDF7'
  },
  [subsOptions.DESIGN_SOFTWARE]: {
    key: 'subscriptionCategories.design_software',
    color: '#C02323',
    bgColor: '#F8E9E9'
  },
  [subsOptions.DEVELOPMENT_SOFTWARE]: {
    key: 'subscriptionCategories.development_software',
    color: '#176BA0',
    bgColor: '#E7F0F5'
  },
  [subsOptions.DIGITAL_ADVERTISING_PLATFORMS]: {
    key: 'subscriptionCategories.digital_advertising_platforms',
    color: '#EA7369',
    bgColor: '#FCF1F0'
  },
  [subsOptions.ERP_SOFTWARE]: {
    key: 'subscriptionCategories.erp_software',
    color: '#EA7369',
    bgColor: '#FCF1F0'
  },
  [subsOptions.GOVERNANCE_RISK]: {
    key: 'subscriptionCategories.governance_risk_&_compliance_software',
    color: '#AF4BCE',
    bgColor: '#F7EDFA'
  },
  [subsOptions.HOSTING_PROVIDERS]: {
    key: 'subscriptionCategories.hosting_providers',
    color: '#EF7E32',
    bgColor: '#FDF2EA'
  },
  [subsOptions.HR_SOFTWARE]: {
    key: 'subscriptionCategories.hr_software',
    color: '#AF4BCE',
    bgColor: '#F7EDFA'
  },
  [subsOptions.IOT_MANAGEMENT_PLATFORMS]: {
    key: 'subscriptionCategories.iot_management_platforms',
    color: '#EABD3B',
    bgColor: '#FCF8EB'
  },
  [subsOptions.IT_INFRASTRUCTURE_SOFTWARE]: {
    key: 'subscriptionCategories.it_infrastructure_software',
    color: '#DB4CB2',
    bgColor: '#FBEDF7'
  },
  [subsOptions.IT_MANAGEMENT_SOFTWARE]: {
    key: 'subscriptionCategories.it_management_software',
    color: '#176BA0',
    bgColor: '#E7F0F5'
  },
  [subsOptions.MARKETING_SOFTWARE]: {
    key: 'subscriptionCategories.marketing_software',
    color: '#EF7E32',
    bgColor: '#FDF2EA'
  },
  [subsOptions.OFFICE_SOFTWARE]: {
    key: 'subscriptionCategories.office_software',
    color: '#C02323',
    bgColor: '#F8E9E9'
  },
  [subsOptions.SALES_TOOLS]: {
    key: 'subscriptionCategories.sales_tools',
    color: '#03AB93',
    bgColor: '#E5F6F4'
  },
  [subsOptions.SECURITY_SOFTWARE]: {
    key: 'subscriptionCategories.security_software',
    color: '#19AADE',
    bgColor: '#E8F6FB'
  },
  [subsOptions.SUPPY_CHAIN_SOFTWARE]: {
    key: 'subscriptionCategories.supply_chain_&_logistics_software',
    color: '#AF4BCE',
    bgColor: '#F7EDFA'
  },
  [subsOptions.VERTICAL_INDUSTRY_SOFTWARE]: {
    key: 'subscriptionCategories.vertical_industry_software',
    color: '#19AADE',
    bgColor: '#E8F6FB'
  },
};

/**
 * Returns an options list for bill type selection, marking the active value.
 * @param {*} activeValue - Currently selected bill type value.
 * @returns {Array<{value: *, label: string}>} Array of bill type option objects.
 * @example
 * BillOptions(subscriptionBillTypesConstants.MONTHLY);
 */
export const BillOptions = (activeValue) => OptionsList(billTypeKeys, activeValue);

/**
 * Returns an options list for subscription review type selection, marking the active value.
 * @param {*} activeValue - Currently selected review type value.
 * @returns {Array<{value: *, label: string}>} Array of review type option objects.
 * @example
 * SubscriptionReviewTypeOptions(subscriptionReviewTypesConstants.PAID);
 */
export const SubscriptionReviewTypeOptions = (activeValue) => OptionsList(subscriptionReviewTypeKeys, activeValue);

/**
 * Returns an options list for payment type selection with the NOT_APPLICABLE option always last.
 * @param {*} activeValue - Currently selected payment type value.
 * @returns {Array<{value: *, label: string}>} Array of payment type option objects.
 * @example
 * PaymentOptions(subscriptionPaymentTypesConstants.CARD);
 */
export const PaymentOptions = (activeValue) => {
  const list = OptionsList(paymentTypeKeys, activeValue);
  return list.filter(i => i.value !== NOT_APPLICABLE).concat(list.filter(i => i.value === NOT_APPLICABLE));
};

export const filterBankingPaymentOptions = (options, isBankingEnabled) =>
  isBankingEnabled ? options : options.filter(o => o.value !== CARD && o.value !== WIRE);

/**
 * Returns an options list for card limit period type selection, marking the active value.
 * @param {*} activeValue - Currently selected card limit period value.
 * @returns {Array<{value: *, label: string}>} Array of card limit period option objects.
 * @example
 * CardLimitPeriodTypeOptions(cardLimitPeriodConstants.MONTH);
 */
export const CardLimitPeriodTypeOptions = (activeValue) => OptionsList(cardLimitPeriodTypeKeys, activeValue);

/**
 * Returns an options list for contract renewal period type selection, marking the active value.
 * @param {*} activeValue - Currently selected contract renewal period value.
 * @returns {Array<{value: *, label: string}>} Array of contract renewal period option objects.
 * @example
 * ContractRenewalPeriodTypeOptions(subscriptionContractRenewalPeriodTypesConstants.ONE_MONTH);
 */
export const ContractRenewalPeriodTypeOptions = (activeValue) => OptionsList(contractRenewalPeriodTypeKeys, activeValue);

/**
 * Returns an options list for contract renewal reminder type selection, marking the active value.
 * @param {*} activeValue - Currently selected contract renewal reminder value.
 * @returns {Array<{value: *, label: string}>} Array of contract renewal reminder option objects.
 * @example
 * ContractRenewalReminderTypeOptions(subscriptionContractRenewalReminderTypesConstants.ONE_MONTH);
 */
export const ContractRenewalReminderTypeOptions = (activeValue) => OptionsList(contractRenewalReminderTypeKeys, activeValue);

/**
 * Returns an options list for payment frequency type selection, marking the active value.
 * @param {*} activeValue - Currently selected payment frequency value.
 * @returns {Array<{value: *, label: string}>} Array of payment frequency option objects.
 * @example
 * PaymentFrequencyTypeOptions(subscriptionPaymentFrequencyTypesConstants.ONE_MONTH);
 */
export const PaymentFrequencyTypeOptions = (activeValue) => OptionsList(paymentFrequencyTypeKeys, activeValue);

/**
 * React hook-style function that returns a sorted, translated list of subscription category options.
 * @returns {Array<{value: string, label: string, color: string, bgColor: string}>} Alphabetically sorted category options.
 * @example
 * const categories = SubscriptionsCategoryOptions();
 */
export const SubscriptionsCategoryOptions = () => {
  const [t] = useTranslation('main');
  let categoriesList = Object.entries(categories);
  categoriesList = categoriesList.map(category => {
    const value = category[0];
    const props = category[1];
    const {bgColor, color} = props;
    return {
      bgColor,
      color,
      value,
      label: t(props.key)
    }
  });
  categoriesList.sort((a, b) => {
    if (a.label < b.label) return -1;
    if (a.label > b.label) return 1;
    return 0;
  });
  return categoriesList;
}

/**
 * Returns subscription status options enriched with colored status line images.
 * @param {*} activeValue - Currently selected status value.
 * @returns {Array<{value: *, label: string, image: React.ReactElement}>} Status option objects with image.
 * @example
 * StatusOptions(subscriptionStatusesConstants.ACTIVE);
 */
export const StatusOptions = (activeValue) => {
  const options = OptionsList(subscriptionStatusKeys, activeValue);
  return options.map(option => ({
    ...option,
    image: <StatusLine color={subscriptionsHelpers.getSubscriptionStatusColor(option.value)} />
  }));
};

/**
 * Collection of utility helpers for subscription data formatting, status resolution, and UI rendering.
 * @namespace subscriptionsHelpers
 * @example
 * import { subscriptionsHelpers } from './subscriptions.helpers';
 * subscriptionsHelpers.getSubscriptionStatus({ statusCode: 'active' });
 */
export const subscriptionsHelpers = {
  /**
   * Returns the i18n key for a card limit period type from its constant key.
   * @param {string} key - Card limit period constant key.
   * @returns {string} i18n translation key string.
   * @example
   * subscriptionsHelpers.getCardLimitType(cardLimitPeriodConstants.MONTH);
   */
  getCardLimitType: (key) => gObjProp(cardLimitPeriodTypeKeys, key),
  /**
   * Returns the i18n key for a bill type from its constant key.
   * @param {string} key - Bill type constant key.
   * @returns {string} i18n translation key string.
   * @example
   * subscriptionsHelpers.getBillType(subscriptionBillTypesConstants.MONTHLY);
   */
  getBillType: (key) => gObjProp(billTypeKeys, key),
  /**
   * Returns the i18n key for a contract renewal period type from its constant key.
   * @param {string} key - Contract renewal period constant key.
   * @returns {string} i18n translation key string.
   * @example
   * subscriptionsHelpers.getContractRenewalPeriodType(subscriptionContractRenewalPeriodTypesConstants.ONE_MONTH);
   */
  getContractRenewalPeriodType: (key) => gObjProp(contractRenewalPeriodTypeKeys, key),
  /**
   * Returns the i18n key for a duration type from its constant key.
   * @param {string} key - Duration type constant key.
   * @returns {string} i18n translation key string.
   * @example
   * subscriptionsHelpers.getDurationType(subscriptionDurationTypesConstants.MONTHS);
   */
  getDurationType: (key) => gObjProp(durationTypeKeys, key),
  /**
   * Returns the i18n key for a payment frequency type from its constant key.
   * @param {string} key - Payment frequency constant key.
   * @returns {string} i18n translation key string.
   * @example
   * subscriptionsHelpers.getPaymentFrequencyType(subscriptionPaymentFrequencyTypesConstants.TWELVE_MONTHS);
   */
  getPaymentFrequencyType: (key) => gObjProp(paymentFrequencyTypeKeys, key),
  /**
   * Returns the i18n key for a payment type from its constant key.
   * @param {string} key - Payment type constant key.
   * @returns {string} i18n translation key string.
   * @example
   * subscriptionsHelpers.getPaymentType(subscriptionPaymentTypesConstants.CARD);
   */
  getPaymentType: (key) => gObjProp(paymentTypeKeys, key),
  /**
   * Returns the i18n translation key for a subscription status, or undefined if the status is not found.
   * @param {string} key - Subscription status constant key.
   * @returns {string|undefined} i18n translation key or undefined.
   * @example
   * subscriptionsHelpers.getStatusType(subscriptionStatusesConstants.ACTIVE);
   */
  getStatusType: (key) => {
    const status = gObjProp(subscriptionStatuses, key);
    return status ? status.key : undefined;
  },
  /**
   * Returns the next formatted payment date for a subscription, advancing past-due dates to the following month.
   * @param {Object} subscription - Subscription object with next_payment and expected_first_payment fields.
   * @param {string} [outputFormat='DD MMM YYYY'] - Moment.js format string for the output date.
   * @param {string} [emptyText='N.A.'] - Fallback text returned when no date is available.
   * @returns {string} Formatted next payment date or emptyText.
   * @example
   * subscriptionsHelpers.getNextPaymentDate(subscription, 'DD/MM/YYYY', 'No date');
   */
  getNextPaymentDate: (subscription, outputFormat = 'DD MMM YYYY', emptyText = 'N.A.') => {
    const {expected_first_payment: exceptedFirstPaymentDate, next_payment: nextPaymentDate} = subscription;
    let date = objectHelpers.isNaV(nextPaymentDate) && exceptedFirstPaymentDate ? exceptedFirstPaymentDate : nextPaymentDate;
    if (date) {
      let momentDateObj = dayjs(dateHelpers.getTimestampDateObject(date));
      let today = dayjs().endOf('day');
      if (!momentDateObj.isAfter(today)) {
        // dayjs is immutable, so this has to be reassigned — under moment the chain mutated
        // `momentDateObj` in place and the line below formatted the mutated value. Units are applied
        // largest-first to match how moment ordered an object set (H1MVP-4742).
        const nextPayment = momentDateObj
          .set('year', today.year())
          .set('month', today.month())
          .set('date', momentDateObj.date())
          .add(1, 'M');
        return nextPayment.format(outputFormat);
      }
      return dateHelpers.getTimestampDate(date, outputFormat);
    } else {
      return emptyText;
    }
  },
  /**
   * Returns a formatted date string from a subscription field, or a placeholder when the date is absent.
   * @param {Object} subs - Subscription object.
   * @param {string} key - Property key to read the date from.
   * @param {string} [placeholder=''] - Value returned when the date is absent.
   * @returns {string} Formatted date string in 'DD MMM YYYY' format or placeholder.
   * @example
   * subscriptionsHelpers.getSubscriptionDate(subscription, 'start_date', 'N/A');
   */
  getSubscriptionDate: (subs, key, placeholder = '') => {
    let date = gObjProp(subs, key);
    return date ? dateHelpers.getTimestampDate(date, 'DD MMM YYYY') : placeholder;
  },
  /**
   * Returns the effective status code for a subscription, substituting OVERBUDGET when the budget is exceeded while active.
   * @param {Object} subscription - Subscription object with status and budget_status fields.
   * @returns {string} Effective status code.
   * @example
   * subscriptionsHelpers.getSubscriptionStatusCode({ status: 'active', budget_status: 'overbudget' }); // 'overbudget'
   */
  getSubscriptionStatusCode: (subscription) => {
    let status = subscription?.status;
    const budgetStatus = subscription?.budget_status;
    if (status === ACTIVE && budgetStatus === OVERBUDGET) status = budgetStatus;
    return status;
  },
  /**
   * Returns the status object (key and color) for a given subscription status code.
   * @param {Object} params - Parameters object.
   * @param {string} params.statusCode - Status code to resolve.
   * @returns {{key: string|null, color: string}} Status object with i18n key and hex color.
   * @example
   * subscriptionsHelpers.getSubscriptionStatus({ statusCode: 'active' });
   */
  getSubscriptionStatus: ({statusCode}) => objectHelpers.getStatus(statusCode, subscriptionStatuses),
  /**
   * Returns the hex color code for a given subscription status, defaulting to grey for unknown statuses.
   * @param {string} status - Subscription status constant.
   * @returns {string} Hex color string.
   * @example
   * subscriptionsHelpers.getSubscriptionStatusColor('active'); // '#03AB93'
   */
  getSubscriptionStatusColor: (status) => {
    const colors = {
      [ABOUT_TO_RENEW]: '#A020F0',
      [ACTIVE]: '#03AB93',
      [DELETED]: '#EA7369',
      [INACTIVE]: '#FFCC00',
      [OVERBUDGET]: '#FAAD14',
      [PENDING]: '#D9D9D9',
      [TRIAL]: '#515FFF',
      [UNTRACKED]: '#9493A6'
    }
    return colors[status] || '#9493A6'
  },
  /**
   * Builds the props object for a form item based on field error state.
   * @param {Object} fieldErrors - Map of field names to their error state objects.
   * @param {string} propName - Form field name to look up.
   * @param {string} className - Base CSS class applied to the form item.
   * @param {Object} [additionalProps={}] - Extra props merged into the result (e.g. isRequired override).
   * @returns {{className: string, isRequired: boolean, error: boolean, message: string}} Form item props.
   * @example
   * subscriptionsHelpers.getFormItemProps(fieldErrors, 'budget_limit', 'my-class');
   */
  getFormItemProps: (fieldErrors, propName, className, additionalProps = {}) => {
    const {className: cName, error, message} = fieldErrors[propName];
    return {
      className: `${className} ${cName}`,
      isRequired: gObjProp(additionalProps, 'isRequired', true),
      error,
      message
    }
  },
  /**
   * Returns the category metadata object (bgColor, color, key) for a subscription category, falling back to a default.
   * @param {string} category - Category constant key.
   * @returns {{bgColor: string, color: string, key: string}} Category metadata.
   * @example
   * subscriptionsHelpers.getSubscriptionCategory(subsOptions.ANALYTICS_TOOLS_SOFTWARE);
   */
  getSubscriptionCategory: (category) => {
    return categories.hasOwnProperty(category) ? categories[category] : {
      bgColor: '#E5F6F4',
      color: '#03AB93',
      key: 'undefined'
    };
  },
  /**
   * Returns true when the load-more action should be enabled based on whether more data exists before the loaded range.
   * @param {string|null} availableDate - The earliest available data date.
   * @param {number} months - Number of months already loaded (subtracted from the current date).
   * @returns {boolean} True when there are more months to load.
   * @example
   * subscriptionsHelpers.checkIsEnabledLoadMore('2023-01-01', 3);
   */
  checkIsEnabledLoadMore: (availableDate, months) => {
    let isEnabled = false;
    if (availableDate) {
      const loadedMonthDate = dayjs().date(1).subtract(months, 'months');
      // `.set({date: 1})` discarded its result here, which worked only because moment mutated in
      // place. `isSameOrBefore` is also moment-only — dayjs needs a plugin for it — so it is
      // expressed directly as "not after" (H1MVP-4742).
      const from = dayjs(availableDate).date(1);
      isEnabled = !from.isAfter(loadedMonthDate);
    }
    return isEnabled;
  },
  /**
   * Returns the expanded row content component for a subscription table row.
   * @param {Object} record - Subscription table row record.
   * @returns {React.ReactElement} ExpandedSubscriptionRow component.
   * @example
   * subscriptionsHelpers.getExpandedRowRender(subscriptionRecord);
   */
  getExpandedRowRender: (record) => (
    <ExpandedSubscriptionRow
      subscription={record}
    />
  ),
  /**
   * Returns the Ant Design Table expandable configuration object for subscription rows.
   * @returns {{expandIcon: Function, expandedRowRender: Function, expandRowByClick: boolean, rowExpandable: Function}} Expandable props.
   * @example
   * <Table expandable={subscriptionsHelpers.getExpandableProps()} />
   */
  getExpandableProps: () => ({
    expandIcon: ({ expandable, expanded, onExpand, record }) => expandable && (
      <ChevronDownIcon
        className={`expanded-table-icon ${expanded ? 'expanded' : ''}`}
        onClick={e => onExpand(record, e)}
      />
    ),
    expandedRowRender: subscriptionsHelpers.getExpandedRowRender,
    expandRowByClick: true,
    rowExpandable: (record) => !subscriptionsHelpers.isAuthorizedSubscription(record)
  }),
  /**
   * Builds the query parameters object for a subscription transaction request.
   * @param {Object} query - Base query parameters.
   * @param {number} subtractMonthsCount - Number of months to subtract from the current date for month/year params.
   * @param {boolean} [disableMonthSelection=false] - When true, uses a fixed limit instead of month/year params.
   * @returns {Object} Complete query parameters object for the API request.
   * @example
   * subscriptionsHelpers.getTransactionRequestQuery({ vendor_id: 5 }, 2);
   */
  getTransactionRequestQuery: (query, subtractMonthsCount, disableMonthSelection = false) => {
    let data = { sort_order: 'desc', ...query };
    if (disableMonthSelection) {
      data = { limit: transactionsRequestLimit, ...data }
    } else {
      data = {
        ...subscriptionsHelpers.getYearMonthQueryParams(subtractMonthsCount),
        ...data
      }
    }
    return data;
  },
  /**
   * Returns a { month, year } object for the date obtained by subtracting the given number of months from today.
   * @param {number} subtractMonthsCount - Number of months to subtract from the current date.
   * @returns {{month: number, year: number}} Month (1-based) and year values.
   * @example
   * subscriptionsHelpers.getYearMonthQueryParams(1); // e.g. { month: 2, year: 2026 }
   */
  getYearMonthQueryParams: (subtractMonthsCount) => {
    // The subtraction has to be assigned. Discarded, it silently returned the current month and
    // every caller would have queried the wrong period (H1MVP-4742).
    const currentDate = dayjs().subtract(subtractMonthsCount, 'months');
    return {
      month: currentDate.month() + 1,
      year: currentDate.year(),
    }
  },
  /**
   * Returns a SubscriptionStatusMark component for the given subscription data.
   * @param {Object} data - Subscription data object.
   * @returns {React.ReactElement} SubscriptionStatusMark element.
   * @example
   * subscriptionsHelpers.getSubscriptionStatusMark(subscription);
   */
  getSubscriptionStatusMark: (data) => {
    const statusCode = subscriptionsHelpers.getSubscriptionStatusCode(data);
    return (
      <SubscriptionStatusMark statusCode={statusCode} />
    );
  },
  /**
   * Returns an Avatar with a Tooltip showing the subscription owner's full name and initials.
   * @param {Object} data - Subscription data object with an owner property.
   * @returns {React.ReactElement} Tooltip-wrapped Avatar element.
   * @example
   * subscriptionsHelpers.getSubscriptionOwner(subscription);
   */
  getSubscriptionOwner: (data) => teamsHelpers.getUserAvatar(data?.owner),
  /**
   * Returns a usage variant string ('empty', 'low', 'medium', 'high') based on a numeric usage percentage.
   * @param {number} value - Usage percentage value.
   * @returns {'empty'|'low'|'medium'|'high'} Usage variant label.
   * @example
   * subscriptionsHelpers.getUsageVariant(30); // 'medium'
   */
  getUsageVariant: (value) => {
    let variant = 'empty';
    if (value > 1) {
      if (value <= 25) {
        variant = 'low';
      } else if (value <= 50 && value > 25) {
        variant = 'medium';
      } else {
        variant = 'high';
      }
    }
    return variant;
  },
  /**
   * Returns a Tooltip-wrapped Usage component displaying login count as a visual usage indicator.
   * @param {number} value - Number of logins.
   * @returns {React.ReactElement} Centered div with Tooltip and Usage component.
   * @example
   * subscriptionsHelpers.getLoginsCount(42);
   */
  getLoginsCount: (value) => {
    let variant = subscriptionsHelpers.getUsageVariant(value);
    return (
      <div className='flex-center-center'>
        <Tooltip styles={{body: {textAlign: 'center'}}} placement='bottom' title={value}>
          <Usage variant={variant} />
        </Tooltip>
      </div>
    );
  },
  /**
   * Returns a services options list, prepending a custom entry when the search text is a valid URL not already in the list.
   * @param {Array<{domain: string, name: string}>} services - Existing services to display as options.
   * @param {string} [searchedText=''] - User-entered search text potentially containing a domain/URL.
   * @returns {Array<{domain: string, name: string}>} Options list, possibly with the searched domain prepended.
   * @example
   * subscriptionsHelpers.getServicesOptionsList(services, 'https://example.com');
   */
  getServicesOptionsList: (services = [], searchedText = '') => {
    const safeServices = Array.isArray(services) ? services : [];
    let options = [...safeServices];
    searchedText = subscriptionsHelpers.getSearchedDomainName(searchedText);
    if (
      locationHelpers.isValidUrl(searchedText) &&
      (
        !Boolean(safeServices.length) ||
        safeServices.find(s => s.domain === searchedText || s.name === searchedText) === undefined
      )
    ) {
      options = [
        {domain: searchedText, name: searchedText},
        ...options
      ];
    }
    return options;
  },
  /**
   * Returns the filtered list of columns that are either always available or included in selectedColumns.
   * @param {Array<Object>} columns - All column definitions.
   * @param {string[]} [selectedColumns=[]] - Keys of columns the user has selected to display.
   * @returns {Array<Object>} Filtered column definitions.
   * @example
   * subscriptionsHelpers.getAvailableColumns(allColumns, ['name', 'status']);
   */
  getAvailableColumns: (columns, selectedColumns = []) =>
    elementHelpers.getAvailableTableColumns({
      alwaysAvailableColumnKeys,
      columns,
      selectedColumns
    }),
  /**
   * Returns the right-side column settings content element for a subscriptions table filter bar.
   * @param {Object} params - Parameters object.
   * @param {Object} [params.buttonProps={}] - Additional props to pass to the settings button.
   * @param {Array<Object>} [params.columns=[]] - All column definitions for the settings panel.
   * @param {React.ReactNode} params.rightSideContent - Additional content rendered inside the button.
   * @param {string[]} params.selectedKeys - Currently selected column keys.
   * @param {Function} params.onChange - Callback invoked when the selected columns change.
   * @returns {React.ReactElement} Right-side column selector content.
   * @example
   * subscriptionsHelpers.getSettingsRightSideContent({ columns, rightSideContent, selectedKeys, onChange });
   */
  getSettingsRightSideContent: ({buttonProps = {}, columns = [], rightSideContent, selectedKeys, onChange}) =>
    elementHelpers.getSettingsRightSideContent({
      alwaysAvailableColumnKeys,
      buttonProps,
      columns,
      onChange,
      selectedKeys,
      rightSideContent,
    }),
  /**
   * Retrieves stored column selections for a named subscriptions tab from local storage.
   * @param {string} name - Tab identifier used to namespace the storage key.
   * @returns {string[]|null} Stored column key array or null if not set.
   * @example
   * subscriptionsHelpers.getStoredTabColumns('active');
   */
  getStoredTabColumns: (name) => ls.get(`${storedTabColumnsPrefix}-${name}`),
  /**
   * Persists column selections for a named subscriptions tab to local storage.
   * @param {string} name - Tab identifier used to namespace the storage key.
   * @param {string[]} value - Array of selected column keys to store.
   * @returns {void}
   * @example
   * subscriptionsHelpers.storeTabColumns('active', ['name', 'status']);
   */
  storeTabColumns: (name, value) => ls.set(`${storedTabColumnsPrefix}-${name}`, value),
  /**
   * Returns stored column selections for a table key, falling back to a set of default keys.
   * @param {string} tableKey - Key identifying the table/tab.
   * @param {string[]} defaultKeys - Default column keys used when no stored selection is found.
   * @returns {string[]} Column keys to display.
   * @example
   * subscriptionsHelpers.getDefaultTableColumns('active', ['name', 'status', 'amount']);
   */
  getDefaultTableColumns: (tableKey, defaultKeys) => subscriptionsHelpers.getStoredTabColumns(tableKey) || defaultKeys,
  /**
   * Returns an Ant Design Avatar.Group element displaying up to six service logos.
   * @param {Array<{logo_url: string, name: string, url: string}>} services - List of service objects to display.
   * @returns {React.ReactElement|undefined} Avatar.Group element, or undefined when maxCount is less than 1.
   * @example
   * subscriptionsHelpers.getServicesAvatarGroup(services);
   */
  getServicesAvatarGroup: (services) => {
    const maxCount = 6;
    if (maxCount < 1) return ;

    return (
      <Avatar.Group max={{ count: 6 }} size={40}>
        {services.map((s, key) => {
          const logo = s.logo_url;
          return (
            <Avatar
              key={key}
              src={logo || undefined}
              style={{backgroundColor: logo ? 'unset' : '#BFBFBF'}}
            >
              {textHelpers.getInitials(s.name || s.url)}
            </Avatar>
          )
        })}
      </Avatar.Group>
    )
  },
  /**
   * Returns a React fragment listing service avatars and names for use inside a Tooltip.
   * @param {Array<{logo_url: string, name: string, url: string}>} services - List of service objects to display.
   * @returns {React.ReactElement|undefined} Fragment of Space elements, or undefined when maxCount is less than 1.
   * @example
   * subscriptionsHelpers.getTooltipServices(services);
   */
  getTooltipServices: (services) => {
    const maxCount = 6;
    const avatarStyle = {backgroundColor: '#BFBFBF'};
    if (maxCount < 1) return ;

    return (
      <>
        {services.map((s, key) => {
          const serviceName = s.name || s.url;
          return (
            <Space size={4} align='center' key={key}>
              <Avatar
                key={key}
                src={s.logo_url || undefined}
                size={16}
                style={avatarStyle}
              >
                {textHelpers.getInitials(serviceName)}
              </Avatar>
              <span>{serviceName}</span>
            </Space>
          )
        })}
      </>
    )
  },
  /**
   * Returns the extra content (edit/save/cancel controls) for a subscription details form panel.
   * @param {Object} params - Parameters object.
   * @param {Function} params.t - i18n translation function.
   * @param {boolean} params.isEnableEdit - Whether editing is permitted.
   * @param {boolean} params.isEditMode - Whether the form is currently in edit mode.
   * @param {Function} params.setIsEditMode - State setter to toggle edit mode.
   * @param {Function} params.handleOnSave - Callback invoked when the save button is clicked.
   * @returns {React.ReactElement|false} Space with buttons in edit mode, EditButton otherwise, or false when editing is disabled.
   * @example
   * subscriptionsHelpers.getDetailsFormExtra({ t, isEnableEdit: true, isEditMode: false, setIsEditMode, handleOnSave });
   */
  getDetailsFormExtra: ({t, isEnableEdit, isEditMode, setIsEditMode, handleOnSave}) => {
    return isEnableEdit ?
      isEditMode ? (
        <Space size='middle'>
          <Button
            onClick={() => setIsEditMode(false)}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleOnSave}
            type='primary'
          >
            {t('save')}
          </Button>
        </Space>
      ) : (
        <EditButton
          className='edit-btn'
          onClick={() => isEnableEdit && setIsEditMode(true)}
        />
      ) : false;
  },
  /**
   * Returns true when a subscription has a DELETED or TERMINATED status.
   * @param {Object} subscription - Subscription object with a status property.
   * @returns {boolean} True when the subscription is deleted or terminated.
   * @example
   * subscriptionsHelpers.isDeletedSubscription({ status: 'deleted' }); // true
   */
  isDeletedSubscription: (subscription) => [DELETED, TERMINATED].includes(subscription.status),
  /**
   * Returns true when a subscription has an authorized (non-audited, non-deleted, non-restricted) status.
   * @param {Object} subscription - Subscription object with a status property.
   * @returns {boolean} True when the subscription status is not in the excluded set.
   * @example
   * subscriptionsHelpers.isAuthorizedSubscription({ status: 'active' }); // true
   */
  isAuthorizedSubscription: (subscription) => ![AUDITED, DELETED, RESTRICTED, TERMINATED, UNTRACKED, UNMANAGED].includes(subscription.status),
  /**
   * Returns true when a status belongs to the audited group (AUDITED, RESTRICTED, or UNMANAGED).
   * @param {string} status - Status constant to check.
   * @returns {boolean} True when status is AUDITED, RESTRICTED, or UNMANAGED.
   * @example
   * subscriptionsHelpers.isAuditedStatus('audited'); // true
   */
  isAuditedStatus: (status) => [AUDITED, RESTRICTED, UNMANAGED].includes(status),
  /**
   * Fetches subscription emails when they have not been loaded yet, updating loading state throughout.
   * @param {Object} params - Parameters object.
   * @param {Event} params.e - DOM event used to stop propagation.
   * @param {Object} params.subscription - Subscription object with an id property.
   * @param {Function} params.setSubscriptionEmails - State setter for the subscription emails map.
   * @param {Object} params.subscriptionEmails - Current subscription emails state map.
   * @param {Function} params.getSubscriptionEmails - API function to fetch emails for a subscription ID.
   * @returns {void}
   * @example
   * subscriptionsHelpers.handleLoadSubscriptionEmails({ e, subscription, setSubscriptionEmails, subscriptionEmails, getSubscriptionEmails });
   */
  handleLoadSubscriptionEmails: ({e, subscription, setSubscriptionEmails, subscriptionEmails, getSubscriptionEmails}) => {
    elementHelpers.stopPropagation(e);
    const subscriptionId = subscription.id;
    if (!subscriptionEmails.hasOwnProperty(subscriptionId)) {
      setSubscriptionEmails({...subscriptionEmails, loading: true});
      getSubscriptionEmails(
        subscriptionId,
        (data) => {
          setSubscriptionEmails({
            ...subscriptionEmails,
            loading: false, data: data.emails || []
          });
        },
        () => {
          setSubscriptionEmails({
            ...subscriptionEmails,
            loading: false, data: []
          });
        }
      );
    }
  },
  /**
   * Returns an Ant Design dropdown menu item descriptor with a translated label and optional icon.
   * @param {Object} params - Parameters object.
   * @param {string} params.key - Menu item key; also used as the translation suffix.
   * @param {React.ReactNode} params.icon - Icon element shown before the label.
   * @param {Function} params.t - i18n translation function.
   * @param {Object} rest - Additional props passed to the inner Space element.
   * @returns {{key: string, label: React.ReactElement}} Ant Design menu item descriptor.
   * @example
   * subscriptionsHelpers.getDropdownMenuItem({ key: 'edit', icon: <EditIcon />, t });
   */
  getDropdownMenuItem: ({key, icon, t, ...rest}) => {
    return ({
      key,
      label: (
        <StyledSubscriptionTableTabMenuItemSpace
          aling='center'
          size='small'
          {...rest}
        >
          {icon}{t(`subscriptions:actions.${key.toLowerCase()}`)}
        </StyledSubscriptionTableTabMenuItemSpace>
      )
    })
  },
  /**
   * Returns a Dropdown component with an ellipsis trigger icon for subscription row actions.
   * @param {Object} menuProps - Ant Design Dropdown menu props (items, onClick, etc.).
   * @returns {React.ReactElement} Dropdown element with ellipsis icon trigger.
   * @example
   * subscriptionsHelpers.getDropdownMenu({ items: menuItems, onClick: handleMenuClick });
   */
  getDropdownMenu: (menuProps) => (
    <Dropdown
      menu={menuProps}
      placement='bottomRight'
    >
      <span>
        <EllipsisVerticalIcon
          className='ellipsis-icon'
          onClick={elementHelpers.stopPropagation}
        />
      </span>
    </Dropdown>
  ),
  /**
   * Returns true when the subscription detail panel should allow editing based on status and edit flag.
   * @param {string} statusCode - Current subscription status code.
   * @param {boolean} [edit=true] - Whether edit mode is generally permitted.
   * @returns {boolean} True when editing is enabled for this status.
   * @example
   * subscriptionsHelpers.isEnablePanelEdit('active'); // true
   */
  isEnablePanelEdit: (statusCode, edit = true) => edit && ![CARD_ERROR, DELETED, PENDING, TERMINATED].includes(statusCode),
  /**
   * Returns the detail page URL for a given subscription.
   * @param {Object} subscription - Subscription object with an id property.
   * @returns {string} URL string for the subscription detail page.
   * @example
   * subscriptionsHelpers.getSubscriptionDetailsPageUrl({ id: 42 });
   */
  getSubscriptionDetailsPageUrl: (subscription) => `${routes.subscriptionsList}/${subscription?.id}`,
  /**
   * Returns true when a subscription's status is included in the allowed statuses for the specified view.
   * @param {Object} [params={}] - Parameters object.
   * @param {Array<{view: string, statuses: string[]}>} params.viewsStatuses - View-status configuration array.
   * @param {string} params.view - View name to look up.
   * @param {Object} params.subscription - Subscription object with a status property.
   * @returns {boolean} True when the subscription status is valid for the view.
   * @example
   * subscriptionsHelpers.isViewSubscription({ viewsStatuses, view: 'active', subscription });
   */
  isViewSubscription: ({viewsStatuses, view, subscription} = {}) => {
    // `viewsStatuses` is null until its fetch lands (and is reset to null on a company switch).
    const viewDetails = viewsStatuses?.find(v => v.view === view);
    const statuses = viewDetails?.statuses || [];
    return statuses.includes(subscription.status);
  },
  /**
   * Returns the service name or URL for a subscription's associated service.
   * @param {Object} subscription - Subscription object with a service property.
   * @returns {string} Service name or URL, or empty string if absent.
   * @example
   * subscriptionsHelpers.getServiceName({ service: { name: 'Slack' } }); // 'Slack'
   */
  getServiceName: (subscription) => {
    const service = gObjProp(subscription, 'service');
    return gObjProp(service, 'name') || gObjProp(service, 'url');
  },
  /**
   * Returns the display name for a subscription, preferring its friendly_name over the service name.
   * @param {Object} subscription - Subscription object with optional friendly_name and service properties.
   * @returns {string} Friendly name or service name/URL.
   * @example
   * subscriptionsHelpers.getSubscriptionName({ friendly_name: 'Team Slack', service: { name: 'Slack' } }); // 'Team Slack'
   */
  getSubscriptionName: (subscription) => {
    const friendlyName = subscription?.friendly_name;
    return friendlyName || subscriptionsHelpers.getServiceName(subscription);
  },
  /**
   * Strips the protocol and www prefix from a URL string and returns only the domain name.
   * @param {string} value - Raw URL or domain string entered by the user.
   * @returns {string} Bare domain name, or empty string when value is falsy.
   * @example
   * subscriptionsHelpers.getSearchedDomainName('https://www.example.com/path'); // 'example.com'
   */
  getSearchedDomainName: (value) => value ? value.replace(/^(?:https?:\/\/)?(?:www\.)?/i, "").split('/')[0] : '',
  /**
   * Returns the props object for the subscription product autocomplete/input/tooltip fields.
   * @param {Object} params - Parameters object.
   * @param {boolean} params.isLoadingServices - Whether the services list is currently loading.
   * @param {Function} params.t - i18n translation function.
   * @returns {{autocomplete: Object, input: Object, tooltip: Object}} Props for each UI sub-component.
   * @example
   * subscriptionsHelpers.getSubscriptionProductProps({ isLoadingServices: false, t });
   */
  getSubscriptionProductProps: ({isLoadingServices, t}) => ({
    autocomplete: {
      autoClearSearchValue: true
    },
    input: {
      allowClear: !isLoadingServices,
      placeholder: t('subscriptions:modal.addSingleSubscription.placeholder.product'),
      size: 'large',
      suffix: isLoadingServices && <Spin indicator={<LoadingOutlined spin />} size='small' />
    },
    tooltip: {
      icon: <InfoCircleOutlined />,
      styles: {root: {fontSize: 13, width: 'max-content'}},
      placement: 'right',
      title: t('subscriptions:modal.addSingleSubscription.tooltip.product')
    }
  }),
  /**
   * Sums the total spending amount across all services and expenses in a spending object.
   * @param {Object} spending - Spending data with optional services and expenses arrays.
   * @param {string} [amountKey='amount'] - Property name used to read the amount from non-card entries.
   * @returns {number} Total summed amount.
   * @example
   * subscriptionsHelpers.getSpendingServicesAmount(spending, 'amount');
   */
  getSpendingServicesAmount: (spending, amountKey = 'amount') => {
    return [...(spending?.services || []), ...(spending?.expenses || [])]
      .reduce((partialSum, a) => partialSum + (a?.card_id ? a.amount : a[amountKey]) || 0, 0)
  },
  /**
   * Returns a filtered and mapped list of services/expenses with non-zero amounts from a spending object.
   * @param {Object} spending - Spending data with optional services and expenses arrays.
   * @param {string} [amountKey='amount'] - Property name used to read and filter the amount.
   * @returns {Array<{service: {name: string, logo: string}, value: number}>} List of service-amount pairs.
   * @example
   * subscriptionsHelpers.getSpendingServicesList(spending, 'amount');
   */
  getSpendingServicesList: (spending, amountKey = 'amount') => {
    return [...(spending?.services || []), ...(spending?.expenses || [])]
      .filter(s => s[amountKey])
      .map(s => ({service: {name: s.service, logo: s.service_logo}, value: s[amountKey]}))
  },
  /**
   * Enriches actual service data with directional indicators by comparing against expected spending values.
   * @param {Array<{name: string, data: Array<{name: string, value: number}>}>} services - Array of series objects with 'actual' and 'expected' entries.
   * @returns {Array<{name: string, data: Array<{name: string, value: number, direction: 'up'|'down'|null}>}>} Services array with direction added to actual data items.
   * @example
   * subscriptionsHelpers.calculateTooltipServices([{ name: 'actual', data: [...] }, { name: 'expected', data: [...] }]);
   */
  calculateTooltipServices: (services) => {
    const actualServices = services.find(s => s.name === 'actual');
    const expectedServices = services.find(s => s.name === 'expected');

    if (actualServices && actualServices.data && expectedServices) {
      const servicesList = [...actualServices.data];
      actualServices.data = servicesList.map(service => {
        const {name, value} = service;
        const expectedSpend = expectedServices.data.find(d => d.name === name);
        return {
          ...service,
          direction: expectedSpend && expectedSpend.value !== value ? value > expectedSpend.value ? 'up' : 'down' : null
        }
      });
    }
    return services;
  },
  /**
   * Returns the Ant Design Form validation rules for a budget amount field.
   * @param {Function} t - i18n translation function.
   * @returns {Array<Object>} Array of validation rule objects.
   * @example
   * subscriptionsHelpers.getBudgetFieldRules(t);
   */
  getBudgetFieldRules: (t) => ([
    {required: true},
    {
      type: 'number',
      min: 1,
      message: t('validation.lessOrEqual', {value: 1})
    }
  ]),
  /**
   * Returns a React fragment showing the formatted budget amount followed by its bill type translation.
   * @param {Object} params - Parameters object.
   * @param {Function} params.t - i18n translation function.
   * @param {Object} params.data - Subscription form data with budget and billing fields.
   * @returns {React.ReactElement} Fragment with amount span and bill type text.
   * @example
   * subscriptionsHelpers.getBudgetBilledValue({ t, data: formValues });
   */
  getBudgetBilledValue: ({t, data}) => (
    <>
      <span className='amount'>{amountHelpers.getAmountWithCurrencyCode(gObjProp(data, subscriptionFormFields.budgetLimitFieldName))}</span>
      &nbsp;
      {t(subscriptionsHelpers.getBillType(gObjProp(data, subscriptionFormFields.billedFieldName)))}
    </>
  ),
  /**
   * Handles subscription service search input, fetching results when input meets the minimum length requirement.
   * @param {Object} params - Parameters object.
   * @param {Function} params.onSearch - API function to call with the search query.
   * @param {Object} params.services - Current services state object with data and loading properties.
   * @param {Function} params.setServices - State setter for the services object.
   * @param {string} params.value - Raw search input value from the user.
   * @returns {void}
   * @example
   * subscriptionsHelpers.handleSearchSubscriptionService({ onSearch, services, setServices, value: 'slack' });
   */
  handleSearchSubscriptionService: ({onSearch, services, setServices, value}) => {
    const clearServices = () => setServices({data: [], loading: false});
    value = subscriptionsHelpers.getSearchedDomainName(value);

    if (value.length < subscriptionServiceSearchMinLength && Boolean(services.data.length)) {
      clearServices();
    } else if (value !== '' && onSearch) {
      setServices({...services, loading: true});
      onSearch(
        {query: value},
        (data) => setServices({data, loading: false}),
        clearServices
      );
    }
  },
  /**
   * Computes default contract renewal date, period, and reminder values based on the billing frequency.
   * @param {string} billed - Billing type constant (e.g. MONTHLY or YEARLY).
   * @returns {{contract_renewal_date: import('dayjs').Dayjs, contract_renewal_period: string|undefined, contract_renewal_reminder: import('dayjs').Dayjs|undefined}} Default contract renewal field values.
   * @example
   * subscriptionsHelpers.getDefaultContractRenewalFieldsValue(subscriptionBillTypesConstants.MONTHLY);
   */
  getDefaultContractRenewalFieldsValue: (billed) => {
    const now = dayjs();
    let contractRenewalDate = now.clone().add(1, 'year').startOf('year'); // Default to yearly
    let contractRenewalPeriod, contractRenewalReminder;

    if (billed === subscriptionBillTypesConstants.MONTHLY) {
      contractRenewalDate = now.clone().add(1, 'month').startOf('month');
      contractRenewalPeriod = subscriptionContractRenewalPeriodTypesConstants.ONE_MONTH;
      contractRenewalReminder = contractRenewalDate.subtract(2, 'week');
    } else if (billed === subscriptionBillTypesConstants.YEARLY) {
      contractRenewalPeriod = subscriptionContractRenewalPeriodTypesConstants.TWELVE_MONTHS;
      contractRenewalReminder = contractRenewalDate.subtract(3, 'month');
    }

    const isInvalidDate = (date) => {
      if (!date) return true;

      return (
        contractRenewalDate.isSame(date, 'day') ||
        contractRenewalDate.isBefore(date, 'day') ||
        date.isSame(now) ||
        date.isBefore(now)
      )
    }

    if (contractRenewalReminder) {
      contractRenewalReminder = dateHelpers.getNextWorkingDay(contractRenewalReminder);

      if (isInvalidDate(contractRenewalReminder)) {
        const nextDay = dateHelpers.getNextWorkingDay(now.clone().add(1, 'day'));
        contractRenewalReminder = isInvalidDate(nextDay) ? undefined : nextDay;
      }
    }

    return {
      contract_renewal_date: contractRenewalDate,
      contract_renewal_period: contractRenewalPeriod,
      contract_renewal_reminder: contractRenewalReminder,
    };
  },
  /**
   * Builds the contract renewal part of the subscription creation request payload.
   * @param {Object} props
   * @param {string} props.billed - Billing type constant, used to resolve the payment frequency.
   * @param {Object|null} props.contractFile - Selected contract file ({file: base64 string, name}) or null.
   * @param {Object} props.formData - Submitted form values with the contract renewal fields.
   * @returns {Object} Contract renewal request values.
   * @example
   * subscriptionsHelpers.getContractRenewalRequestValues({billed, contractFile, formData});
   */
  getContractRenewalRequestValues: ({billed, contractFile, formData} = {}) => ({
    [subscriptionFormFields.contractFileFieldName]: contractFile ? fileHelpers.cutFileData(contractFile.file) : undefined,
    contract_renewal_date: subscriptionsHelpers.getContractDate(formData?.contract_renewal_date),
    contract_renewal_period: formData?.contract_renewal_period,
    contract_renewal_reminder: subscriptionsHelpers.getContractDate(formData?.contract_renewal_reminder),
    payment_frequency: subscriptionsHelpers.getPaymentFrequencyValue(billed)
  }),
  /**
   * Returns the default payment frequency constant that corresponds to the given billing type.
   * @param {string} billed - Billing type constant (MONTHLY or YEARLY).
   * @returns {string|undefined} Corresponding payment frequency constant, or undefined for other billing types.
   * @example
   * subscriptionsHelpers.getPaymentFrequencyValue(subscriptionBillTypesConstants.YEARLY);
   */
  getPaymentFrequencyValue: (billed) => {
    let value;
    if (billed === subscriptionBillTypesConstants.MONTHLY) {
      value = subscriptionPaymentFrequencyTypesConstants.ONE_MONTH;
    } else if (billed === subscriptionBillTypesConstants.YEARLY) {
      value = subscriptionPaymentFrequencyTypesConstants.TWELVE_MONTHS;
    }
    return value;
  },
  /**
   * Returns the total payment amount for a subscription based on its billing type.
   * @param {Object|null} subscription - Subscription object with billed and total payment fields.
   * @returns {number} Total payment amount, or 0 when subscription is null or the field is missing.
   * @example
   * subscriptionsHelpers.getSubscriptionTotalPayment(subscription);
   */
  getSubscriptionTotalPayment: (subscription) => {
    if (!subscription) return 0;

    const billed = subscription?.billed;
    const total_key = {
      [subscriptionBillTypesConstants.MONTHLY]: 'total_payment_month',
      [subscriptionBillTypesConstants.YEARLY]: 'total_payment_year',
      [subscriptionBillTypesConstants.PASS_AS_YOU_GO]: 'total_payment_fixed',
    }[billed];
    return subscription[total_key] || 0;
  },
  /**
   * Returns the Ant Design tooltip configuration object for a subscription form label.
   * @param {Object} [params={}] - Parameters object.
   * @param {string} params.key - Tooltip key; used as the translation suffix or displayed directly.
   * @param {Function} [params.translation] - i18n translation function; when provided, translates the key.
   * @returns {{icon: React.ReactElement, styles: Object, placement: string, title: string}} Tooltip config.
   * @example
   * subscriptionsHelpers.getLabelTooltip({ key: 'budgetLimit', translation: t });
   */
  getLabelTooltip: ({key, translation} = {}) => ({
    icon: <InfoCircleOutlined />,
    styles: {root: {fontSize: 13, width: 'max-content'}},
    placement: 'right',
    title: translation ? translation(`subscriptions:tooltips.${key}`) : key
  }),
  /**
   * Returns payment options enriched with an info tooltip on the CARD option label.
   * @param {Object} [params={}] - Parameters object.
   * @param {Array<{value: *, label: string}>} [params.options=[]] - Base payment option objects.
   * @param {Function} [params.translation] - i18n translation function for the tooltip text.
   * @returns {Array<{value: *, label: string|React.ReactElement}>} Options array with tooltip label on the CARD entry.
   * @example
   * subscriptionsHelpers.getPaymentOptionsWithTooltip({ options: PaymentOptions(), translation: t });
   */
  getPaymentOptionsWithTooltip: ({options = [], translation} = {}) => {
    if (!Array.isArray(options)) return [];

    const renderLabelWithTooltip = (label, translation) => (
      <Space>
        <span>{label}</span>
        <Tooltip
          styles={{ root: { fontSize: 13, maxWidth: 300 } }}
          title={translation('subscriptions:modal.addSingleSubscription.placeholder.card')}
        >
          <InfoCircleOutlined />
        </Tooltip>
      </Space>
    );

    return options.map(({ value, label, ...rest }) => ({
      ...rest,
      value,
      label: value === CARD ? renderLabelWithTooltip(label, translation) : label
    }));
  },
  /**
   * Syncs the card limit field to the budget limit value when conditions for hard-limit card payment are met.
   * @param {Object} [params={}] - Parameters object.
   * @param {number} params.budgetLimit - New budget limit value.
   * @param {number} params.cardLimit - Current card limit value.
   * @param {boolean} [params.isCardPaymentType=false] - Whether payment type is CARD.
   * @param {boolean} [params.isSelectedHardLimit=false] - Whether hard limit mode is selected.
   * @param {Object} params.form - Ant Design Form instance.
   * @returns {void}
   * @example
   * subscriptionsHelpers.updateCardLimitOnBudgetChange({ budgetLimit: 500, cardLimit: 400, isCardPaymentType: true, isSelectedHardLimit: true, form });
   */
  updateCardLimitOnBudgetChange: ({
    budgetLimit,
    cardLimit,
    isCardPaymentType = false,
    isSelectedHardLimit = false,
    form,
  } = {}) => {
    if (!(isCardPaymentType && isSelectedHardLimit && budgetLimit !== cardLimit && form)) return;

    form.setFieldValue(subscriptionFormFields.cardLimitFieldName, budgetLimit);
  },
  /**
   * Updates the card limit period form field to match the billing frequency when hard-limit card payment is active.
   * @param {Object} [params={}] - Parameters object.
   * @param {string} params.billedLimitPeriod - New billing frequency constant.
   * @param {boolean} [params.isCardPaymentType=false] - Whether payment type is CARD.
   * @param {boolean} [params.isSelectedHardLimit=false] - Whether hard limit mode is selected.
   * @param {Object} params.form - Ant Design Form instance.
   * @returns {void}
   * @example
   * subscriptionsHelpers.updateCardLimitPeriodOnBillingChange({ billedLimitPeriod: subscriptionBillTypesConstants.MONTHLY, isCardPaymentType: true, isSelectedHardLimit: true, form });
   */
  updateCardLimitPeriodOnBillingChange: ({
    billedLimitPeriod,
    isCardPaymentType = false,
    isSelectedHardLimit = false,
    form,
  } = {}) => {
    if (!isCardPaymentType || !isSelectedHardLimit || !form) return;

    const cardLimitType = cardsHelpers.getCardLimitTypeFromBilled(billedLimitPeriod);
    form.setFieldValue(subscriptionFormFields.cardLimitPeriodFieldName, cardLimitType);
  },
  /**
   * Validates budget and card limit form values, setting field errors and returning whether validation passed.
   * @param {Object} [params={}] - Parameters object.
   * @param {number} params.budgetLimit - Current budget limit value.
   * @param {number} params.cardLimit - Current card limit value.
   * @param {Object} params.form - Ant Design Form instance.
   * @param {string} params.paymentType - Selected payment type constant.
   * @param {Function} params.translate - i18n translation function.
   * @returns {boolean} True when all limit validations pass.
   * @example
   * subscriptionsHelpers.validateFormLimitValues({ budgetLimit: 100, cardLimit: 100, form, paymentType: 'card', translate: t });
   */
  validateFormLimitValues: ({
    budgetLimit,
    cardLimit,
    form,
    paymentType,
    translate,
  } = {}) => {
    let validate = false;
    if (!form || !translate) return validate;

    const errors = [];

    if (paymentType !== subscriptionPaymentTypesConstants.FREE && !budgetLimit) {
      errors.push({
        name: subscriptionFormFields.budgetLimitFieldName,
        errors: [translate('validation.fieldIsRequired')],
      });
    }

    if (errors.length) {
      form.setFields(errors);
    } else {
      validate = true;
    }

    return validate;
  },
  /**
   * Returns the Ant Design DatePicker props for a contract date field, disabling past dates.
   * @returns {{disabledDate: Function, panelRender: Function, showToday: boolean, style: Object}} DatePicker configuration props.
   * @example
   * <DatePicker {...subscriptionsHelpers.contractDateFieldProps()} />
   */
  contractDateFieldProps: () => ({
    ...kycHelpers.getDatePickerProps(),
    disabledDate: (current) => current && current < dayjs().endOf('day'),
    panelRender: dateHelpers.getDatePickerPanelRender,
    showToday: false,
    style: {width: '100%'}
  }),
  /**
   * Converts a Dayjs date object to a Unix timestamp set to 08:00:00, or returns null when no date is provided.
   * @param {import('dayjs').Dayjs|null} date - Dayjs date object to convert.
   * @returns {number|null} Unix timestamp at 08:00:00 on the given date, or null.
   * @example
   * subscriptionsHelpers.getContractDate(dayjs('2026-06-01'));
   */
  getContractDate: (date) => {
    if (!date) return null;

    return date.hour(8).minute(0).second(0).unix();
  },
  /**
   * Maps a list of subscriptions to Ant Design Select option objects with Avatar labels.
   * @param {Object[]} [subscriptions=[]] - Array of subscription objects.
   * @returns {Array<{value: string|number, label: React.ReactElement, optionLabel: string}>} Select option objects.
   * @example
   * subscriptionsHelpers.getSubscriptionOptions(subscriptions);
   */
  getSubscriptionOptions: (subscriptions = []) => {
    return subscriptions
      .map(subscription => {
        const logo = subscription?.service?.logo || undefined;
        const name = subscriptionsHelpers.getSubscriptionName(subscription);
        return {
          value: subscription.id,
          label: (
            <Space className='d-flex-child'>
              <Avatar src={logo}>
                {textHelpers.getInitials(name)}
              </Avatar>
              {name}
            </Space>
          ),
          optionLabel: name
        }
      });
  },
  /**
   * Returns a NameWithLink component for a subscription, or null when no subscription is provided.
   * @param {Object|null} subscription - Subscription object.
   * @param {string|null} [customUrl] - Url used instead of the subscription details page url. Pass `null` to render the name without a link.
   * @returns {React.ReactElement|null} NameWithLink element or null.
   * @example
   * subscriptionsHelpers.getSubscriptionNameWithLink(subscription);
   * subscriptionsHelpers.getSubscriptionNameWithLink(subscription, '/cards/user-1/card-1');
   */
  getSubscriptionNameWithLink: (subscription, customUrl) => {
    if (!subscription) return null;
    const name = subscriptionsHelpers.getSubscriptionName(subscription);
    const url = customUrl === undefined
      ? subscriptionsHelpers.getSubscriptionDetailsPageUrl(subscription)
      : customUrl;
    return <NameWithLink name={name} url={url} />;
  },
  /**
   * Returns true when adding the expense amount to the subscription's total payment would exceed the budget limit.
   * @param {Object} [params={}] - Parameters object.
   * @param {Object} params.expense - Expense object with an amount property.
   * @param {Object} params.subscription - Subscription object with budget limit and total payment fields.
   * @returns {boolean} True when the combined spend exceeds the budget limit.
   * @example
   * subscriptionsHelpers.isSubscriptionOverBudget({ expense, subscription });
   */
  isSubscriptionOverBudget: ({expense, subscription} = {}) => {
    const isExpenseInBilledPeriod = subscriptionsHelpers.isExpenseInBilledPeriod({expense, subscription});
    if (!isExpenseInBilledPeriod) return false;

    const subscriptionSpent = subscriptionsHelpers.getSubscriptionTotalPayment(subscription) || 0;
    const expenseAmount = expense?.amount || 0;
    const totalSpent = subscriptionSpent + expenseAmount;
    const budgetLimit = subscription[subscriptionFormFields.budgetLimitFieldName] || 0;
    return totalSpent > budgetLimit;
  },
  /**
   * Returns true when the expense dates fall within the current billing period of the subscription.
   * @param {Object} [params={}] - Parameters object.
   * @param {Object} params.expense - Expense object whose dates are evaluated.
   * @param {Object} params.subscription - Subscription object with a billed property.
   * @returns {boolean} True when at least one expense date is within the current billing period.
   * @example
   * subscriptionsHelpers.isExpenseInBilledPeriod({ expense, subscription });
   */
  isExpenseInBilledPeriod: ({expense, subscription} = {}) => {
    if (!expense) return false;

    const period = subscription?.billed;

    if (period === subscriptionBillTypesConstants.PASS_AS_YOU_GO) {
      return true;
    }

    const {start: expenseStartDate, end: expenseEndDate} = expensesHelpers.getExpenseDates(expense) || {};
    const now = new Date();

    const dates = [expenseStartDate, expenseEndDate]
      .map(d => (d ? new Date(d) : null))
      .filter(d => d && !Number.isNaN(d.getTime()));

    if (!dates.length) return false;

    const isSameMonth = (d) =>
      d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    const isSameYear = (d) => d.getFullYear() === now.getFullYear();

    if (period === subscriptionBillTypesConstants.YEARLY) {
      return dates.some(isSameYear);
    } else if (period === subscriptionBillTypesConstants.MONTHLY) {
      return dates.some(isSameMonth);
    }

    return true;
  }
}
